import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Section from '../components/Section';
import {BottomNavigator} from '../components/Navbar';
import usePodcasts from '../hooks/usePodcasts';

const REWIND = 10;
const FORWARD = 30;
const SPEEDS = [1, 1.25, 1.5, 1.75, 2, 0.75];

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/* ---- Inline SVG icons (no emoji, per brand guidelines) ---- */
const Svg = ({children, filled, ...rest}) => (
    <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'}
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>{children}</svg>
);
const IconPlay = () => <Svg filled><path d="M8 5.14v13.72c0 .82.9 1.31 1.58.86l10.29-6.86a1.03 1.03 0 0 0 0-1.72L9.58 4.28A1.03 1.03 0 0 0 8 5.14Z"/></Svg>;
const IconPause = () => <Svg filled><rect x="6" y="4.5" width="4.2" height="15" rx="1.4"/><rect x="13.8" y="4.5" width="4.2" height="15" rx="1.4"/></Svg>;
const IconBack = () => <Svg><path d="M11.5 5.5 7 9l4.5 3.5"/><path d="M7 9h7.5a4.5 4.5 0 0 1 0 9H8"/></Svg>;
const IconForward = () => <Svg><path d="M12.5 5.5 17 9l-4.5 3.5"/><path d="M17 9H9.5a4.5 4.5 0 0 0 0 9H16"/></Svg>;
const IconPrev = () => <Svg filled><path d="M7 5.5a1 1 0 0 1 2 0v5.2l7.4-5.06A1 1 0 0 1 18 6.46v11.08a1 1 0 0 1-1.6.82L9 13.3v5.2a1 1 0 1 1-2 0Z"/></Svg>;
const IconNext = () => <Svg filled><path d="M17 5.5a1 1 0 0 0-2 0v5.2L7.6 5.64A1 1 0 0 0 6 6.46v11.08a1 1 0 0 0 1.6.82L15 13.3v5.2a1 1 0 1 0 2 0Z"/></Svg>;
const IconVolume = () => <Svg><path d="M4 9v6h4l5 4V5L8 9H4Z"/><path d="M16.5 8.5a5 5 0 0 1 0 7"/><path d="M19 6a8.5 8.5 0 0 1 0 12"/></Svg>;
const IconMuted = () => <Svg><path d="M4 9v6h4l5 4V5L8 9H4Z"/><path d="m17 9 4 4M21 9l-4 4"/></Svg>;
const IconSearch = () => <Svg><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></Svg>;

const PodcastCard = memo(({episode, isCurrent, isPlaying, onSelect}) => {
    const [artError, setArtError] = useState(false);
    return (
        <button
            type="button"
            className={`PodCard${isCurrent ? ' PodCard--current' : ''}`}
            onClick={() => onSelect(episode.index)}
            aria-label={`${isCurrent && isPlaying ? 'Pause' : 'Play'} ${episode.title}`}
        >
            <span className="PodCard__art">
                {episode.artworkUrl && !artError
                    ? <img src={episode.artworkUrl} alt={`Cover art for ${episode.title}`} loading="lazy" onError={() => setArtError(true)} />
                    : <span className="PodCard__art-fallback" aria-hidden="true">L</span>}
                <span className="PodCard__fab" aria-hidden="true">{isCurrent && isPlaying ? <IconPause /> : <IconPlay />}</span>
            </span>
            <span className="PodCard__cat">{episode.category}</span>
            <span className="PodCard__title">{episode.title}</span>
            <span className="PodCard__meta">
                {isCurrent && isPlaying
                    ? <span className="PodCard__eq" aria-hidden="true"><span/><span/><span/></span>
                    : null}
                {isCurrent && isPlaying ? 'Now playing' : 'Episode'}
            </span>
        </button>
    );
});

const PodcastPage = () => {
    const {categories, loading, error, refresh} = usePodcasts();

    const {episodes, topics} = useMemo(() => {
        const eps = [];
        const tps = [];
        categories.forEach((cat) => {
            tps.push(cat.title);
            cat.episodes.forEach((ep) => {
                eps.push({...ep, category: ep.topic || cat.title});
            });
        });
        return {episodes: eps.map((ep, index) => ({...ep, index})), topics: tps};
    }, [categories]);

    const [activeCat, setActiveCat] = useState('All');
    const [query, setQuery] = useState('');
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [seeking, setSeeking] = useState(null);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [speedIdx, setSpeedIdx] = useState(0);

    const audioRef = useRef(null);
    const autoplayRef = useRef(false);

    const current = currentIndex >= 0 ? episodes[currentIndex] : null;

    const view = useMemo(() => {
        const q = query.trim().toLowerCase();
        return episodes.filter((ep) => {
            const catOk = activeCat === 'All' || ep.category === activeCat;
            const qOk = !q || ep.title.toLowerCase().includes(q) || ep.category.toLowerCase().includes(q);
            return catOk && qOk;
        });
    }, [episodes, activeCat, query]);

    /* Load source when the selected episode changes */
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !current) {
            return;
        }
        audio.src = current.audioUrl;
        audio.load();
        setCurrentTime(0);
        setDuration(0);
        if (autoplayRef.current) {
            audio.play().catch(() => { /* autoplay may be blocked until a user gesture */ });
        }
    }, [current]);

    /* Reflect volume / mute / speed onto the element */
    useEffect(() => { if (audioRef.current) { audioRef.current.volume = volume; } }, [volume]);
    useEffect(() => { if (audioRef.current) { audioRef.current.muted = muted; } }, [muted]);
    useEffect(() => { if (audioRef.current) { audioRef.current.playbackRate = SPEEDS[speedIdx]; } }, [speedIdx]);

    const selectEpisode = useCallback((index) => {
        if (index === currentIndex) {
            const audio = audioRef.current;
            if (audio) { audio.paused ? audio.play().catch(() => {}) : audio.pause(); }
            return;
        }
        autoplayRef.current = true;
        setCurrentIndex(index);
    }, [currentIndex]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!current) {
            if (view.length) { selectEpisode(view[0].index); }
            return;
        }
        if (!audio) { return; }
        audio.paused ? audio.play().catch(() => {}) : audio.pause();
    }, [current, view, selectEpisode]);

    const skip = useCallback((amount) => {
        const audio = audioRef.current;
        if (!audio) { return; }
        const target = audio.currentTime + amount;
        audio.currentTime = Math.max(0, Number.isFinite(audio.duration) ? Math.min(audio.duration, target) : target);
    }, []);

    const neighbor = useCallback((dir) => {
        if (!view.length) { return; }
        const pos = view.findIndex((ep) => ep.index === currentIndex);
        if (pos === -1) { selectEpisode(view[0].index); return; }
        const next = pos + dir;
        if (next < 0 || next >= view.length) { return; }
        selectEpisode(view[next].index);
    }, [view, currentIndex, selectEpisode]);

    /* Persist and restore playback position per episode */
    const onTimeUpdate = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) { return; }
        setCurrentTime(audio.currentTime);
        if (current) {
            try { window.localStorage.setItem(`lavita-podcast-position-${current.id}`, String(audio.currentTime)); } catch { /* storage may be unavailable */ }
        }
    }, [current]);

    const onLoadedMetadata = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !Number.isFinite(audio.duration)) { return; }
        setDuration(audio.duration);
        if (current) {
            try {
                const saved = Number(window.localStorage.getItem(`lavita-podcast-position-${current.id}`));
                if (Number.isFinite(saved) && saved > 0 && saved < audio.duration) {
                    audio.currentTime = saved;
                    setCurrentTime(saved);
                }
            } catch { /* storage may be unavailable */ }
        }
    }, [current]);

    /* Media Session (lock screen / hardware media keys) */
    useEffect(() => {
        if (!current || !('mediaSession' in navigator)) { return; }
        try {
            /* eslint-disable-next-line no-undef */
            navigator.mediaSession.metadata = new MediaMetadata({
                title: current.title,
                artist: 'Lavita Podcast',
                album: current.category,
                artwork: current.artworkUrl ? [{src: current.artworkUrl, sizes: '512x512', type: 'image/png'}] : [],
            });
            navigator.mediaSession.setActionHandler('play', () => audioRef.current?.play().catch(() => {}));
            navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
            navigator.mediaSession.setActionHandler('seekbackward', () => skip(-REWIND));
            navigator.mediaSession.setActionHandler('seekforward', () => skip(FORWARD));
            navigator.mediaSession.setActionHandler('previoustrack', () => neighbor(-1));
            navigator.mediaSession.setActionHandler('nexttrack', () => neighbor(1));
        } catch { /* Media Session is best-effort */ }
    }, [current, skip, neighbor]);

    /* Keyboard shortcuts (ignored while typing in the search field) */
    useEffect(() => {
        const onKey = (event) => {
            const tag = event.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target.isContentEditable) { return; }
            switch (event.key) {
                case ' ': case 'k': event.preventDefault(); togglePlay(); break;
                case 'ArrowLeft': case 'j': skip(-REWIND); break;
                case 'ArrowRight': case 'l': skip(FORWARD); break;
                case 'ArrowUp': event.preventDefault(); setVolume((v) => Math.min(1, v + 0.05)); setMuted(false); break;
                case 'ArrowDown': event.preventDefault(); setVolume((v) => Math.max(0, v - 0.05)); break;
                case 'm': setMuted((m) => !m); break;
                case 'n': neighbor(1); break;
                case 'p': neighbor(-1); break;
                default: break;
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [togglePlay, skip, neighbor]);

    const featured = episodes[0];
    const displayTime = seeking != null ? seeking : currentTime;
    const progressPct = duration ? (displayTime / duration) * 100 : 0;
    const volumePct = muted ? 0 : volume * 100;

    return (
        <div className="Page PodcastPage">
            <Section className="PodcastSection" id="podcast">
                {/* ---------- Hero ---------- */}
                <div className="PodHero">
                    <div className="PodHero__copy">
                        <span className="PodHero__eyebrow"><span className="PodHero__dot" aria-hidden="true"/>The Lavita Podcast</span>
                        <h1 className="PodHero__title">Longevity, <span className="PodHero__accent">decoded.</span></h1>
                        <p className="PodHero__lede">
                            Short, sharp episodes on the science of living better — AI-driven diagnostics,
                            the microbiome, biological age, and the frontier of personalized medicine.
                        </p>
                        <div className="PodHero__cta">
                            <button type="button" className="PodBtn PodBtn--primary" onClick={() => featured && selectEpisode(featured.index)} disabled={!featured}>
                                <IconPlay /> Play latest
                            </button>
                            <a className="PodBtn PodBtn--ghost" href="#episodes">Browse all episodes</a>
                        </div>
                        <div className="PodHero__stats">
                            <div className="PodStat"><span className="PodStat__n">{episodes.length || '—'}</span><span className="PodStat__l">Episodes</span></div>
                            <div className="PodStat"><span className="PodStat__n">{topics.length || '—'}</span><span className="PodStat__l">Topics</span></div>
                            <div className="PodStat"><span className="PodStat__n">100%</span><span className="PodStat__l">Science-backed</span></div>
                        </div>
                    </div>
                    {featured && (
                        <div className="PodHero__featured">
                            {featured.artworkUrl && <img src={featured.artworkUrl} alt={`Cover art for ${featured.title}`} />}
                            <div className="PodHero__featured-overlay">
                                <span className="PodTag">Featured</span>
                                <span className="PodHero__featured-cat">{featured.category}</span>
                                <span className="PodHero__featured-title">{featured.title}</span>
                            </div>
                            <button type="button" className="PodFab" onClick={() => selectEpisode(featured.index)}
                                    aria-label={`Play ${featured.title}`}>
                                {currentIndex === featured.index && isPlaying ? <IconPause /> : <IconPlay />}
                            </button>
                        </div>
                    )}
                </div>

                {/* ---------- Library ---------- */}
                <div className="PodLibrary" id="episodes">
                    <div className="PodLibrary__head">
                        <h2>All episodes</h2>
                        <div className="PodSearch">
                            <span className="PodSearch__icon" aria-hidden="true"><IconSearch /></span>
                            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                                   placeholder="Search episodes or topics…" aria-label="Search episodes" />
                        </div>
                    </div>

                    <div className="PodFilters" role="group" aria-label="Filter by topic">
                        {['All', ...topics].map((cat) => (
                            <button key={cat} type="button" className="PodChip"
                                    aria-pressed={activeCat === cat} onClick={() => setActiveCat(cat)}>
                                {cat}
                            </button>
                        ))}
                    </div>

                    {loading && <p className="PodStatus">Loading episodes…</p>}
                    {error && (
                        <div className="PodStatus PodStatus--error">
                            <p>{error}</p>
                            <button type="button" onClick={refresh}>Try again</button>
                        </div>
                    )}
                    {!loading && !error && view.length === 0 && (
                        <p className="PodStatus">No episodes match your search. Try another topic.</p>
                    )}

                    <div className="PodGrid">
                        {view.map((episode) => (
                            <PodcastCard key={episode.id} episode={episode}
                                         isCurrent={episode.index === currentIndex}
                                         isPlaying={isPlaying} onSelect={selectEpisode} />
                        ))}
                    </div>
                </div>

                {/* ---------- About ---------- */}
                <div className="PodAbout" id="about">
                    <h2>About the Cast</h2>
                    <p>
                        Lavita audio podcast brings you curated, frontier content from the leading medical
                        and healthcare peer-reviewed journal publications at your fingertips. The topics span
                        everything from AI-Driven Healthcare to Longevity Medicine, and each episode distills a
                        single frontier topic into a focused listen for the Lavita community.
                    </p>
                </div>
            </Section>

            <BottomNavigator next={{to: '/news', title: 'News'}} />

            {/* ---------- Sticky player ---------- */}
            <div className={`PodPlayer${current ? ' PodPlayer--active' : ''}`} role="region" aria-label="Audio player">
                <audio
                    ref={audioRef}
                    preload="metadata"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => neighbor(1)}
                    onTimeUpdate={onTimeUpdate}
                    onLoadedMetadata={onLoadedMetadata}
                />
                <div
                    className="PodPlayer__topline"
                    style={{'--pct': `${progressPct}%`}}
                    aria-hidden="true"
                />
                <div className="PodPlayer__inner">
                    <div className="PodNowPlaying">
                        {current?.artworkUrl && <img src={current.artworkUrl} alt="" />}
                        <div className="PodNowPlaying__text">
                            <div className="PodNowPlaying__title">{current ? current.title : 'Select an episode'}</div>
                            <div className="PodNowPlaying__cat">{current ? current.category : 'Lavita Podcast'}</div>
                        </div>
                    </div>

                    <div className="PodControls">
                        <div className="PodTransport">
                            <button type="button" className="PodCtrl" onClick={() => neighbor(-1)} aria-label="Previous episode" title="Previous (p)"><IconPrev /></button>
                            <button type="button" className="PodCtrl PodCtrl--skip" onClick={() => skip(-REWIND)} aria-label="Rewind 10 seconds" title="Rewind 10s (←)"><IconBack /><span className="PodCtrl__label">10</span></button>
                            <button type="button" className="PodCtrl PodCtrl--play" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'} title="Play / pause (space)">{isPlaying ? <IconPause /> : <IconPlay />}</button>
                            <button type="button" className="PodCtrl PodCtrl--skip" onClick={() => skip(FORWARD)} aria-label="Skip forward 30 seconds" title="Skip 30s (→)"><IconForward /><span className="PodCtrl__label">30</span></button>
                            <button type="button" className="PodCtrl" onClick={() => neighbor(1)} aria-label="Next episode" title="Next (n)"><IconNext /></button>
                        </div>
                        <div className="PodScrub">
                            <span className="PodTime PodTime--cur">{formatTime(displayTime)}</span>
                            <input
                                className="PodRange"
                                type="range"
                                min="0"
                                max={duration || 0}
                                step="1"
                                value={Math.min(displayTime, duration || 0)}
                                style={{'--pct': `${progressPct}%`}}
                                onChange={(e) => setSeeking(Number(e.target.value))}
                                onMouseUp={(e) => { if (audioRef.current) { audioRef.current.currentTime = Number(e.target.value); } setSeeking(null); }}
                                onTouchEnd={(e) => { if (audioRef.current && e.target) { audioRef.current.currentTime = Number(e.target.value); } setSeeking(null); }}
                                onKeyUp={(e) => { if (audioRef.current) { audioRef.current.currentTime = Number(e.target.value); } setSeeking(null); }}
                                aria-label="Seek"
                            />
                            <span className="PodTime PodTime--dur">{formatTime(duration)}</span>
                        </div>
                    </div>

                    <div className="PodPlayer__right">
                        <button type="button" className="PodSpeed" onClick={() => setSpeedIdx((i) => (i + 1) % SPEEDS.length)} aria-label="Playback speed">{SPEEDS[speedIdx]}×</button>
                        <div className="PodVolume">
                            <button type="button" className="PodCtrl PodCtrl--vol" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Unmute' : 'Mute'} title="Mute (m)">{muted || volume === 0 ? <IconMuted /> : <IconVolume />}</button>
                            <input
                                className="PodRange PodRange--vol"
                                type="range" min="0" max="100" step="1"
                                value={volumePct}
                                style={{'--pct': `${volumePct}%`}}
                                onChange={(e) => { const v = Number(e.target.value) / 100; setVolume(v); setMuted(v === 0); }}
                                aria-label="Volume"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PodcastPage;
